import { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import utils, { comnEnvs, comnUtils } from "@/comn/utils";
import { Button, Grid, Group, Layout, Page } from "@/comn/components";
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage } from "@/comn/hooks";
import { APIS, URLS, SF_SURV_MGMT_LIST_SRCH, SG_SURV_MGMT_LIST } from "./services/PtliSurvMgmtService";

/*
 * Survey Management List
 * !== 설문 관리 목록 ==!
 */
export const PTLI0418001Q = (props: any): JSX.Element => {
    const { callback } = props;
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptli0060", "auth_ptlianyADM"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변환 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const modal = useModal();
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!

    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0024" }, // 업무구분
            { area: "comnCd", comnCd: "COM0052" }, // 포털유형
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        survMgmtListSrch: useForm({
            defaultSchema: SF_SURV_MGMT_LIST_SRCH,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        survMgmtList: useGrid({
            defaultSchema: SG_SURV_MGMT_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getSurvMgmtList: useFetch({
            api: (page = grid.survMgmtList.page) =>
                APIS.getSurvMgmtList(form.survMgmtListSrch.getValues(), page, grid.survMgmtList.size),
            enabled: comnUtils.isEmpty(form.survMgmtListSrch.errors),
            key: [grid.survMgmtList.page, grid.survMgmtList.size],
            onSuccess: () => {
                setStore(pgeUid, {
                    form: form.survMgmtListSrch.getValues(),
                    page: grid.survMgmtList.page,
                    size: grid.survMgmtList.size,
                });
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00025" }), // Failed to select. !== 조회가 실패하였습니다. ==!
            showToast: true,
        }),

        updateSurvActv: useFetch({
            // 활성화
            api: (data) => APIS.updateSurvActv(data),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.ptl.00843", // Successfully activated. 정상적으로 활성화되었습니다.
                    onConfirm: () => handler.onSrchBtnClick(),
                    onCancel: () => handler.onSrchBtnClick(),
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                modal.openModal({ content: data.errorMessage });
            },
        }),

        updateSurvInactv: useFetch({
            // 비활성화
            api: (data) => APIS.updateSurvInactv(data),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.ptl.00844", // Successfully inactivated. 정상적으로 비활성화되었습니다.
                    onConfirm: () => handler.onSrchBtnClick(),
                    onCancel: () => handler.onSrchBtnClick(),
                });
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                modal.openModal({ content: data.errorMessage });
            },
        }),
    };

    const render = {
        cell: {
            qustnrTtle: (props: any) => {
                const { rowValues, value } = props;
                if (callback) {
                    return (
                        <a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                callback(rowValues);
                            }}
                        >
                            {value}
                        </a>
                    );
                } else {
                    const { rowValues, value } = props;
                    return <Link to={`${URLS.ptli0418004q}/${rowValues.qestnrId}`}>{value}</Link>;
                }
            },
            qustnrActvYn: (props: any) => {
                const { rowValues } = props;

                const isActived = rowValues.qustnrActvYn === "Y";

                return isActived ? (
                    <Button onClick={() => handler.onInactvBtnClick(rowValues)}>{t("B_INACTV")}</Button>
                ) : (
                    <Button onClick={() => handler.onActvBtnClick(rowValues)}>{t("B_ACTV")}</Button>
                );
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onSrchBtnClick: () => {
            grid.survMgmtList.setPage(0);
            fetch.getSurvMgmtList.fetch(0);
        },

        onNewBtnClick: () => navigate(URLS.ptli0418002s),

        onActvBtnClick: (data: any) => {
            modal.openModal({
                content: "msg.ptl.00841", // 설문조사를 활성화 하시겠습니까?? << msg 만들기
                onConfirm: () => {
                    fetch.updateSurvActv.fetch(data);
                },
            });
        },

        onInactvBtnClick: (data: any) => {
            modal.openModal({
                content: "msg.ptl.00842", // 설문조사를 비활성화 하시겠습니까?? << msg 만들기
                onConfirm: () => {
                    fetch.updateSurvInactv.fetch(data);
                },
            });
        },
    };

    /*
     * Declaration Page Init Function
     * !== 화면 초기화 함수 선언  ==!
     */
    useEffect(() => {
        handler.onSrchBtnClick();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.survMgmtListSrch.schema.bsopClsfCd} />
                            <Group.Control {...form.survMgmtListSrch.schema.ptlTpCd} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survMgmtListSrch.schema.qustnrTtle} />
                            <Group.Control {...form.survMgmtListSrch.schema.rgsrUsr} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.survMgmtListSrch.schema.reqDateRnge} controlSize={7} />
                        </Group.Row>
                    </Group.Section>
                    <Layout>
                        <Layout.Left>
                            <Button role={"reset"} onClick={() => form.survMgmtListSrch.reset()} />
                        </Layout.Left>
                        <Layout.Right>
                            <Button
                                role={"search"}
                                onClick={() => {
                                    handler.onSrchBtnClick();
                                }}
                            />
                        </Layout.Right>
                    </Layout>
                </Group.Body>
            </Group>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid
                            {...grid.survMgmtList.grid}
                            data={fetch.getSurvMgmtList.data?.survMgmtList}
                            render={render}
                        />
                    </Group.Section>
                </Group.Body>
            </Group>
            <Layout direction="row">
                <Layout.Left>
                    <Button role={"new"} onClick={handler.onNewBtnClick} />
                </Layout.Left>
            </Layout>
        </Page>
    );
};
